import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Select, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import UnfoldLessOutlinedIcon from '@mui/icons-material/UnfoldLessOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import StarIcon from '@mui/icons-material/Star';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import {
  DOCUMENT,
  documentSelectMenuProps,
  documentSelectSx,
  documentSubTabsSx,
} from '../constants/documentTheme';
import {
  FILM_STRIP,
  INSTANT_CARD,
  RUSTIC_PRINT,
  MEMORIES_LOOKS,
  MEMORIES_TONES,
  describeLookTone,
  getVisualFilterMeta,
  getVisualFilterPresentation,
  normalizeVisualLook,
  normalizeVisualTone,
} from '../constants/memoriesVisualFilters';
import { mainAppTripUrl, memoriesPath, memoriesPublicBase, mediaUrl, isMemoriesHost } from '../utils/memoriesHost';
import MemoriesFilmStripFrames from './MemoriesFilmStripFrames';
import MemoriesGuestGate from './MemoriesGuestGate';
import MemoriesPeoplePanel from './MemoriesPeoplePanel';
import MemoriesSlideshow from './MemoriesSlideshow';
import MemoriesUploadPanel from './MemoriesUploadPanel';

export function frameSearchHaystack(frame) {
  if (!frame) return '';
  const parts = [
    frame.caption,
    frame.context_notes,
    frame.original_filename,
    frame.uploader_display_name,
    frame.uploader_email,
    frame.visual_filter,
    frame.visual_tone,
    frame.frame_no != null ? String(frame.frame_no) : '',
  ];
  if (frame.captured_at) {
    try {
      parts.push(new Date(frame.captured_at).toLocaleString());
    } catch {
      parts.push(String(frame.captured_at));
    }
  }
  return parts.filter(Boolean).join(' ').toLowerCase();
}

export function filterAlbumFrames(frames, search) {
  const query = String(search || '').trim().toLowerCase();
  const list = Array.isArray(frames) ? frames : [];
  if (!query) {
    return { query: '', filtered: list };
  }
  return {
    query,
    filtered: list.filter((frame) => frameSearchHaystack(frame).includes(query)),
  };
}

export const ALBUM_PHOTO_SORTS = [
  'album',
  'newest',
  'oldest',
  'participant',
  'participant_desc',
  'caption',
];

export function frameSortDateMs(frame) {
  const raw = frame?.captured_at || frame?.uploaded_at || null;
  if (!raw) return 0;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : 0;
}

function compareText(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base', numeric: true });
}

/** Sort filtered album frames. Default `album` keeps frame_no / server order. */
export function sortAlbumFrames(frames, sortKey = 'album') {
  const list = Array.isArray(frames) ? [...frames] : [];
  const key = ALBUM_PHOTO_SORTS.includes(sortKey) ? sortKey : 'album';
  if (key === 'album') {
    return list.sort((a, b) => {
      const fa = Number(a?.frame_no) || 0;
      const fb = Number(b?.frame_no) || 0;
      if (fa !== fb) return fa - fb;
      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    });
  }
  if (key === 'newest') {
    return list.sort((a, b) => {
      const diff = frameSortDateMs(b) - frameSortDateMs(a);
      if (diff !== 0) return diff;
      return (Number(b?.id) || 0) - (Number(a?.id) || 0);
    });
  }
  if (key === 'oldest') {
    return list.sort((a, b) => {
      const diff = frameSortDateMs(a) - frameSortDateMs(b);
      if (diff !== 0) return diff;
      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    });
  }
  if (key === 'participant') {
    return list.sort((a, b) => {
      const byName = compareText(a?.uploader_display_name, b?.uploader_display_name);
      if (byName !== 0) return byName;
      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    });
  }
  if (key === 'participant_desc') {
    return list.sort((a, b) => {
      const byName = compareText(b?.uploader_display_name, a?.uploader_display_name);
      if (byName !== 0) return byName;
      return (Number(a?.id) || 0) - (Number(b?.id) || 0);
    });
  }
  // caption
  return list.sort((a, b) => {
    const byCaption = compareText(a?.caption, b?.caption);
    if (byCaption !== 0) return byCaption;
    return (Number(a?.id) || 0) - (Number(b?.id) || 0);
  });
}

export function frameGroupDateKey(frame) {
  const raw = frame?.captured_at || frame?.uploaded_at || null;
  if (!raw) return 'undated';
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return 'undated';
  return new Date(ms).toISOString().slice(0, 10);
}

export function formatFrameGroupDateLabel(dateKey, locale) {
  if (!dateKey || dateKey === 'undated') return null;
  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) return dateKey;
  return new Date(year, month - 1, day).toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function frameGroupCaptionKey(frame) {
  const caption = String(frame?.caption || '').trim();
  if (!caption) return 'none';
  const letter = caption[0].toLocaleUpperCase();
  return /[0-9]/.test(letter) ? '0-9' : letter;
}

/**
 * Group already-sorted frames for labeled sections.
 * Returns [{ key, label, frames }] — label may be a string or a translation descriptor.
 */
export function groupAlbumFrames(frames, sortKey = 'album', { showLabels = true, locale } = {}) {
  const list = Array.isArray(frames) ? frames : [];
  if (!showLabels || list.length === 0) {
    return [{ key: 'all', label: null, frames: list }];
  }

  const key = ALBUM_PHOTO_SORTS.includes(sortKey) ? sortKey : 'album';
  const groups = [];
  const indexByKey = new Map();

  const push = (groupKey, label, frame) => {
    let group = indexByKey.get(groupKey);
    if (!group) {
      group = { key: groupKey, label, frames: [] };
      indexByKey.set(groupKey, group);
      groups.push(group);
    }
    group.frames.push(frame);
  };

  if (key === 'participant' || key === 'participant_desc') {
    list.forEach((frame) => {
      const name = String(frame?.uploader_display_name || '').trim();
      push(
        name ? `person:${name.toLowerCase()}` : 'person:unknown',
        name || { type: 'unknownParticipant' },
        frame
      );
    });
    return groups;
  }

  if (key === 'caption') {
    list.forEach((frame) => {
      const letter = frameGroupCaptionKey(frame);
      push(
        `caption:${letter}`,
        letter === 'none' ? { type: 'noCaption' } : letter,
        frame
      );
    });
    return groups;
  }

  // album / newest / oldest → group by calendar day
  list.forEach((frame) => {
    const dateKey = frameGroupDateKey(frame);
    push(
      `date:${dateKey}`,
      dateKey === 'undated'
        ? { type: 'undated' }
        : formatFrameGroupDateLabel(dateKey, locale) || dateKey,
      frame
    );
  });
  return groups;
}

export function resolveGroupLabel(label, t) {
  if (label == null) return null;
  if (typeof label === 'string') return label;
  if (label.type === 'undated') return t('memories.photoGroupUndated');
  if (label.type === 'unknownParticipant') return t('memories.photoGroupUnknownParticipant');
  if (label.type === 'noCaption') return t('memories.photoGroupNoCaption');
  return String(label);
}


const ALBUM_INTRO_MAX = 2000;
const uploadPrefKey = (slug) => `memories-show-upload:${slug}`;

export function readShowUploadPref(slug, hasPhotos) {
  try {
    const raw = localStorage.getItem(uploadPrefKey(slug));
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    /* ignore */
  }
  return !hasPhotos;
}

export function writeShowUploadPref(slug, show) {
  try {
    localStorage.setItem(uploadPrefKey(slug), show ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** Inline editable Polaroid / film chin caption. Shows look label until a caption is set. */
function ChinCaption({
  caption = '',
  placeholder = '',
  color,
  canEdit = false,
  onSave,
  testId = 'memories-card-caption',
  sx: sxProp = {},
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(caption || '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const hasCaption = Boolean(String(caption || '').trim());
  const display = hasCaption ? caption : placeholder;

  useEffect(() => {
    if (!editing) setDraft(caption || '');
  }, [caption, editing]);

  useEffect(() => {
    if (editing) {
      const el = inputRef.current;
      if (el) {
        el.focus();
        el.select?.();
      }
    }
  }, [editing]);

  const beginEdit = (event) => {
    if (!canEdit || saving) return;
    event.preventDefault();
    event.stopPropagation();
    setDraft(caption || '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(caption || '');
    setEditing(false);
  };

  const commit = async () => {
    if (!editing) return;
    const next = String(draft || '').trim();
    const prev = String(caption || '').trim();
    setEditing(false);
    if (next === prev || !onSave) return;
    setSaving(true);
    try {
      await onSave(next);
    } catch {
      setDraft(caption || '');
    } finally {
      setSaving(false);
    }
  };

  const textSx = {
    width: '100%',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    fontFamily: DOCUMENT.display,
    fontSize: { xs: 11, md: 12 },
    lineHeight: 1.25,
    color: color || '#3D342C',
    fontStyle: hasCaption ? 'italic' : 'normal',
    letterSpacing: hasCaption ? '0.01em' : '0.1em',
    textTransform: hasCaption ? 'none' : 'uppercase',
    fontWeight: hasCaption ? 500 : 600,
    cursor: canEdit ? 'text' : 'inherit',
    borderRadius: 0,
    outline: 'none',
    ...sxProp,
  };

  if (editing) {
    return (
      <TextField
        inputRef={inputRef}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => { commit(); }}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            commit();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            cancelEdit();
          }
        }}
        placeholder={placeholder || t('memories.chinCaptionPlaceholder')}
        disabled={saving}
        multiline
        maxRows={3}
        fullWidth
        variant="standard"
        inputProps={{
          'aria-label': t('memories.chinCaptionEdit'),
          'data-testid': 'memories-chin-caption-input',
          maxLength: 280,
        }}
        sx={{
          width: '100%',
          '& .MuiInputBase-root': {
            fontFamily: DOCUMENT.display,
            fontSize: { xs: 11, md: 12 },
            fontStyle: 'italic',
            color: color || '#3D342C',
            letterSpacing: '0.01em',
            textAlign: 'center',
          },
          '& .MuiInputBase-input': {
            textAlign: 'center',
            p: 0,
          },
          '& .MuiInput-underline:before, & .MuiInput-underline:after': {
            borderBottomColor: color || '#3D342C',
          },
        }}
      />
    );
  }

  if (!display && !canEdit) return null;

  return (
    <Typography
      component="span"
      role={canEdit ? 'button' : undefined}
      tabIndex={canEdit ? 0 : undefined}
      aria-label={canEdit ? t('memories.chinCaptionEdit') : undefined}
      data-testid={hasCaption || !placeholder ? testId : 'memories-filter-badge'}
      onClick={beginEdit}
      onKeyDown={(event) => {
        if (!canEdit) return;
        if (event.key === 'Enter' || event.key === ' ') beginEdit(event);
      }}
      sx={textSx}
    >
      {display || t('memories.chinCaptionPlaceholder')}
    </Typography>
  );
}

const PhotoGrid = ({
  frames,
  onOpen,
  testId,
  photoLabel,
  filterLabelFor,
  selectMode = false,
  selectedIds = null,
  onToggleSelect,
  canEditCaption = false,
  onSaveCaption,
}) => (
  <Box
    data-testid={testId}
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(2, minmax(0, 1fr))',
        sm: 'repeat(3, minmax(0, 1fr))',
        md: 'repeat(4, minmax(0, 1fr))',
        lg: 'repeat(6, minmax(0, 1fr))',
      },
      gap: { xs: 1.5, md: 1.25 },
      mb: 4,
      alignItems: 'start',
    }}
  >
    {frames.map((frame) => {
      const selected = selectMode && selectedIds?.has(frame.id);
      const look = getVisualFilterPresentation(frame.visual_filter, frame.visual_tone);
      const filterLabel = filterLabelFor?.(frame) || '';
      const isFramed = Boolean(look.usesCard || look.usesFilmStrip || look.usesRusticPrint);
      const openOrSelect = () => {
        if (selectMode) onToggleSelect?.(frame);
        else onOpen(frame);
      };
      return (
        <Box
          key={frame.id}
          sx={{
            minWidth: 0,
            textAlign: 'left',
            alignSelf: 'start',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              outline: selected ? `2px solid ${DOCUMENT.accent}` : 'none',
              outlineOffset: 2,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: isFramed ? (look.outerAspectRatio || '88 / 112') : '1',
                backgroundColor: isFramed ? undefined : '#110C0A',
                borderRadius: '2px',
                overflow: 'hidden',
                display: isFramed ? 'flex' : 'block',
                flexDirection: 'column',
                ...(isFramed
                  ? look.frameSx
                  : { border: `1px solid ${selected ? DOCUMENT.accent : DOCUMENT.rule}` }),
              }}
            >
              <Box
                component="button"
                type="button"
                aria-label={[frame.caption || photoLabel, filterLabel].filter(Boolean).join(' · ')}
                aria-pressed={selectMode ? Boolean(selected) : undefined}
                onClick={openOrSelect}
                sx={{
                  p: 0,
                  m: 0,
                  border: 0,
                  background: 'none',
                  cursor: 'pointer',
                  display: 'block',
                  position: 'relative',
                  width: look.usesRusticPrint ? 'auto' : (isFramed ? 'auto' : '100%'),
                  ...(look.usesFilmStrip
                    ? {
                      mx: `${FILM_STRIP.sidePadPct}%`,
                      mt: `${FILM_STRIP.sprocketPadPct}%`,
                      mb: `${FILM_STRIP.sprocketPadPct}%`,
                      flex: '1 1 auto',
                      minHeight: 0,
                      overflow: 'hidden',
                      backgroundColor: 'transparent',
                      alignSelf: 'stretch',
                    }
                    : look.usesRusticPrint
                      ? {
                        position: 'absolute',
                        left: look.inset?.left || RUSTIC_PRINT.inset.left,
                        top: look.inset?.top || RUSTIC_PRINT.inset.top,
                        right: look.inset?.right || RUSTIC_PRINT.inset.right,
                        bottom: look.inset?.bottom || RUSTIC_PRINT.inset.bottom,
                        overflow: 'hidden',
                        backgroundColor: '#2A2218',
                      }
                      : look.usesCard
                        ? {
                          mx: `${INSTANT_CARD.edgePct}%`,
                          mt: `${INSTANT_CARD.edgePct}%`,
                          aspectRatio: INSTANT_CARD.photoAspectRatio || '1',
                          overflow: 'hidden',
                          flexShrink: 0,
                          border: '1px solid rgba(0,0,0,0.12)',
                          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.25)',
                          backgroundColor: '#1A120C',
                          alignSelf: 'stretch',
                        }
                        : { position: 'absolute', inset: 0 }),
                }}
              >
                {look.usesFilmStrip ? (
                  <MemoriesFilmStripFrames
                    src={mediaUrl(frame.thumb_url)}
                    imageSx={look.imageSx}
                    overlaySx={look.overlaySx}
                    toneOverlaySx={look.toneOverlaySx}
                    mode="bg"
                  />
                ) : (
                  <>
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url("${String(mediaUrl(frame.thumb_url) || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"')}")`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        ...look.imageSx,
                      }}
                    />
                    {look.overlaySx && (
                      <Box
                        aria-hidden
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          pointerEvents: 'none',
                          ...look.overlaySx,
                        }}
                      />
                    )}
                    {look.toneOverlaySx && (
                      <Box
                        aria-hidden
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          pointerEvents: 'none',
                          ...look.toneOverlaySx,
                        }}
                      />
                    )}
                  </>
                )}
                {frame.starred && !selectMode && (
                  <StarIcon sx={{ position: 'absolute', top: 6, right: 6, fontSize: 16, color: DOCUMENT.accent, zIndex: 1 }} />
                )}
                {selectMode && (
                  <Box sx={{ position: 'absolute', top: 4, left: 4, zIndex: 2, color: selected ? DOCUMENT.accent : '#fff', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }}>
                    {selected
                      ? <CheckBoxOutlinedIcon sx={{ fontSize: 22 }} />
                      : <CheckBoxOutlineBlankOutlinedIcon sx={{ fontSize: 22 }} />}
                  </Box>
                )}
                {!isFramed && filterLabel && (
                  <Typography
                    component="span"
                    data-testid="memories-filter-badge"
                    sx={{
                      position: 'absolute',
                      left: 6,
                      bottom: 6,
                      zIndex: 2,
                      px: 0.75,
                      py: 0.25,
                      maxWidth: 'calc(100% - 12px)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      fontSize: 9,
                      fontWeight: 600,
                      color: DOCUMENT.ivory,
                      backgroundColor: 'rgba(20, 14, 12, 0.72)',
                      borderRadius: '2px',
                      pointerEvents: 'none',
                    }}
                  >
                    {filterLabel}
                  </Typography>
                )}
              </Box>

              {look.usesRusticPrint && (
                <Box
                  component="img"
                  src={look.overlayUrl || RUSTIC_PRINT.overlayUrl}
                  alt=""
                  aria-hidden
                  data-testid="memories-rustic-print-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'fill',
                    pointerEvents: 'none',
                    zIndex: 3,
                  }}
                />
              )}

              {/* Polaroid chin — editable inline, outside the photo button */}
              {look.usesCard && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: '6%',
                    py: 0.75,
                    textAlign: 'center',
                  }}
                >
                  <ChinCaption
                    caption={frame.caption || ''}
                    placeholder={filterLabel}
                    color={look.captionColor || '#3D342C'}
                    canEdit={canEditCaption && !selectMode}
                    onSave={(next) => onSaveCaption?.(frame, next)}
                  />
                  {frame.caption && filterLabel && (
                    <Typography
                      component="span"
                      data-testid="memories-filter-badge"
                      sx={{
                        mt: 0.35,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontSize: 8,
                        fontWeight: 600,
                        color: look.badgeColor || '#6A5E52',
                        pointerEvents: 'none',
                      }}
                    >
                      {filterLabel}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* Film / rustic caption sits below the frame */}
            {(look.usesFilmStrip || look.usesRusticPrint) && (
              <Box sx={{ mt: 0.75, width: '100%', textAlign: 'center' }}>
                <ChinCaption
                  caption={frame.caption || ''}
                  placeholder={filterLabel}
                  color={DOCUMENT.ink2}
                  canEdit={canEditCaption && !selectMode}
                  onSave={(next) => onSaveCaption?.(frame, next)}
                />
              </Box>
            )}

            {!isFramed && frame.caption && (
              <Box sx={{ mt: 0.75, width: '100%' }}>
                <ChinCaption
                  caption={frame.caption}
                  placeholder=""
                  color={DOCUMENT.ink2}
                  canEdit={canEditCaption && !selectMode}
                  onSave={(next) => onSaveCaption?.(frame, next)}
                  sx={{
                    WebkitLineClamp: 3,
                    fontSize: 12,
                    fontStyle: 'normal',
                    letterSpacing: '0',
                    textTransform: 'none',
                    fontWeight: 400,
                    fontFamily: DOCUMENT.sans,
                    textAlign: 'left',
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      );
    })}
  </Box>
);

const MemoriesAlbumView = ({ slug, shareCode = null, embedded = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [event, setEvent] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const [lightboxZoom, setLightboxZoom] = useState(1);
  const [qrOpen, setQrOpen] = useState(false);
  const [qr, setQr] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  const [shareLinkUrl, setShareLinkUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [contextNotes, setContextNotes] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [albumTab, setAlbumTab] = useState(0);
  const [inviteEmails, setInviteEmails] = useState([]);
  const [inviteDraft, setInviteDraft] = useState('');
  const [pendingInvites, setPendingInvites] = useState([]);
  const [declinedInvites, setDeclinedInvites] = useState([]);
  const [members, setMembers] = useState([]);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  const [peopleBusyId, setPeopleBusyId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [photoSearch, setPhotoSearch] = useState('');
  const [photoSort, setPhotoSort] = useState('album');
  const [showGroupLabels, setShowGroupLabels] = useState(false);
  const [slideshowOpen, setSlideshowOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const photoSearchInputRef = useRef(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [pendingLook, setPendingLook] = useState(null);
  const [pendingTone, setPendingTone] = useState(null);
  const [applyingLook, setApplyingLook] = useState(false);
  const [lookMessage, setLookMessage] = useState('');
  const [showUpload, setShowUpload] = useState(true);
  const [editingIntro, setEditingIntro] = useState(false);
  const [introDraft, setIntroDraft] = useState('');
  const [savingIntro, setSavingIntro] = useState(false);
  const [introMessage, setIntroMessage] = useState('');

  const shareQuery = shareCode ? `?share=${encodeURIComponent(shareCode)}` : '';
  const viewOnly = Boolean(event?.view_only);
  const guestCanUpload = Boolean(event?.guest_can_upload);
  const hasGuestSession = Boolean(event?.has_guest_session);
  // Members always upload; link/QR guests upload after identifying when the album allows it.
  const canUpload = !viewOnly || (guestCanUpload && hasGuestSession);
  // Share viewers who cannot upload yet: offer sign-in / register / guest (if allowed).
  const showGuestGate = Boolean(
    shareCode
    && viewOnly
    && !hasGuestSession
    && (guestCanUpload || Boolean(authUser))
  );

  const loadPeople = useCallback(async (isOwner, isViewOnly = false) => {
    if (isViewOnly) {
      setPendingInvites([]);
      setDeclinedInvites([]);
      setMembers([]);
      return;
    }
    try {
      if (isOwner) {
        const { data } = await api.get(`/memories/events/${slug}/invites`);
        setPendingInvites(data.pending || []);
        setDeclinedInvites(data.declined || []);
        setMembers(data.members || []);
      } else {
        const { data } = await api.get(`/memories/events/${slug}/members`);
        setPendingInvites([]);
        setDeclinedInvites([]);
        setMembers(data.members || []);
      }
    } catch {
      setPendingInvites([]);
      setDeclinedInvites([]);
      setMembers([]);
    }
  }, [slug]);

  const load = useCallback(async () => {
    const [{ data: eventData }, { data: mediaData }] = await Promise.all([
      api.get(`/memories/events/${slug}${shareQuery}`),
      api.get(`/memories/events/${slug}/media${shareQuery}`),
    ]);
    const nextMedia = mediaData.media || [];
    setEvent(eventData.event);
    setMedia(nextMedia);
    setShowUpload(readShowUploadPref(slug, nextMedia.length > 0));
    await loadPeople(Boolean(eventData.event?.is_owner), Boolean(eventData.event?.view_only));
  }, [slug, shareQuery, loadPeople]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || t('memories.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [load, t]);

  useEffect(() => {
    if (!searchOpen) return undefined;
    const id = window.setTimeout(() => {
      photoSearchInputRef.current?.focus?.();
    }, 0);
    return () => window.clearTimeout(id);
  }, [searchOpen]);

  const openPhotoSearch = () => setSearchOpen(true);

  const closePhotoSearch = () => {
    setSearchOpen(false);
    setPhotoSearch('');
  };

  const openQr = async () => {
    const { data } = await api.get(`/memories/events/${slug}/qr`);
    setQr(data);
    setQrOpen(true);
  };

  const copyShareLink = async () => {
    setShareMessage('');
    setShareLinkUrl('');
    try {
      const { data } = await api.get(`/memories/events/${slug}/share`);
      const url = data.viewUrl
        || `${memoriesPublicBase({ tripLinked: Boolean(event?.trip_id) })}/v/${data.code}`;
      setShareLinkUrl(url);

      let copied = false;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(url);
          copied = true;
        }
      } catch {
        copied = false;
      }

      const message = copied ? t('memories.shareLinkCopied') : t('memories.shareLinkReady');
      setShareMessage(message);
      enqueueSnackbar(message, { variant: 'success' });
    } catch (err) {
      const message = err.response?.data?.error || t('memories.shareLinkFailed');
      setShareMessage(message);
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const setUploadVisible = (visible) => {
    setShowUpload(visible);
    writeShowUploadPref(slug, visible);
  };

  const beginEditIntro = () => {
    setIntroDraft(event?.notes || '');
    setIntroMessage('');
    setEditingIntro(true);
  };

  const cancelEditIntro = () => {
    setEditingIntro(false);
    setIntroDraft('');
    setIntroMessage('');
  };

  const saveIntro = async () => {
    if (!event?.is_owner) return;
    setSavingIntro(true);
    setIntroMessage('');
    try {
      const notes = String(introDraft || '').trim().slice(0, ALBUM_INTRO_MAX);
      const { data } = await api.patch(`/memories/events/${slug}`, { notes: notes || null });
      setEvent((prev) => ({ ...prev, ...(data.event || {}), notes: data.event?.notes ?? (notes || null) }));
      setEditingIntro(false);
      setIntroDraft('');
    } catch (err) {
      setIntroMessage(err.response?.data?.error || t('memories.albumIntroFailed'));
    } finally {
      setSavingIntro(false);
    }
  };

  const sendInvites = async () => {
    if (inviteEmails.length === 0) return;
    setInviting(true);
    setInviteMessage('');
    try {
      const { data } = await api.post(`/memories/events/${slug}/invites/email`, { emails: inviteEmails });
      const sentList = data.invites?.sent || [];
      const skippedList = data.invites?.skipped || [];
      const failedList = data.invites?.failed || [];
      const sent = sentList.length;
      const skipped = skippedList.length;
      const failed = failedList.length;
      const resentCount = sentList.filter((item) => item.resent).length;
      const parts = [t('memories.inviteSentSummary', { sent, skipped, failed })];
      if (resentCount > 0) {
        parts.push(t('memories.inviteResentCount', { count: resentCount }));
      }
      skippedList.forEach((item) => {
        if (item.reason === 'self') {
          parts.push(t('memories.inviteSkippedSelf', { email: item.email }));
        } else if (item.reason === 'already_member') {
          parts.push(t('memories.inviteSkippedMember', { email: item.email }));
        }
      });
      failedList.forEach((item) => {
        parts.push(t('memories.inviteFailedEmail', { email: item.email }));
      });
      setInviteMessage(parts.join(' '));
      setInviteEmails([]);
      setInviteDraft('');
      await loadPeople(true);
    } catch (err) {
      setInviteMessage(err.response?.data?.error || t('memories.inviteFailed'));
    } finally {
      setInviting(false);
    }
  };

  const resendInvite = async (invite) => {
    if (!invite?.email) return;
    setPeopleBusyId(`invite-resend-${invite.id}`);
    setInviteMessage('');
    try {
      const { data } = await api.post(`/memories/events/${slug}/invites/email`, { emails: [invite.email] });
      const sent = data.invites?.sent?.length || 0;
      const failed = data.invites?.failed?.length || 0;
      if (sent > 0) {
        setInviteMessage(t('memories.inviteResentOne', { email: invite.email }));
      } else if (failed > 0) {
        setInviteMessage(t('memories.inviteFailedEmail', { email: invite.email }));
      } else {
        const skipped = data.invites?.skipped?.[0];
        if (skipped?.reason === 'already_member') {
          setInviteMessage(t('memories.inviteSkippedMember', { email: invite.email }));
        } else {
          setInviteMessage(t('memories.inviteFailed'));
        }
      }
      await loadPeople(true);
    } catch (err) {
      setInviteMessage(err.response?.data?.error || t('memories.inviteFailed'));
    } finally {
      setPeopleBusyId(null);
    }
  };

  const revokeInvite = async (invite) => {
    setPeopleBusyId(`invite-${invite.id}`);
    try {
      await api.delete(`/memories/events/${slug}/invites/${invite.id}`);
      setPendingInvites((prev) => prev.filter((item) => item.id !== invite.id));
    } catch (err) {
      setInviteMessage(err.response?.data?.error || t('memories.revokeInviteFailed'));
    } finally {
      setPeopleBusyId(null);
    }
  };

  const updateMemberRole = async (member, role) => {
    setPeopleBusyId(`member-role-${member.id}`);
    try {
      const { data } = await api.patch(`/memories/events/${slug}/members/${member.id}/role`, { role });
      const nextRole = data.member?.role || role;
      setMembers((prev) => prev.map((item) => (
        item.id === member.id ? { ...item, ...(data.member || {}), role: nextRole } : item
      )));
    } catch (err) {
      setInviteMessage(err.response?.data?.error || t('memories.memberRoleFailed'));
    } finally {
      setPeopleBusyId(null);
    }
  };

  const removeMember = async (member) => {
    setPeopleBusyId(`member-${member.id}`);
    try {
      await api.delete(`/memories/events/${slug}/members/${member.id}`);
      setMembers((prev) => prev.filter((item) => item.id !== member.id));
      setEvent((prev) => (prev ? {
        ...prev,
        member_count: Math.max(0, Number(prev.member_count || 1) - 1),
      } : prev));
    } catch (err) {
      setInviteMessage(err.response?.data?.error || t('memories.removeAccessFailed'));
    } finally {
      setPeopleBusyId(null);
    }
  };

  const toggleStar = async (frame) => {
    const { data } = await api.post(`/memories/media/${frame.id}/reactions`);
    setMedia((prev) => prev.map((item) => (item.id === frame.id ? { ...item, starred: data.starred } : item)));
    if (lightbox?.id === frame.id) setLightbox({ ...frame, starred: data.starred });
  };

  const saveFrameCaption = async (frame, nextCaption) => {
    if (!frame?.id || viewOnly) return;
    try {
      const { data } = await api.patch(`/memories/media/${frame.id}`, {
        caption: nextCaption,
      });
      if (data.media) {
        setMedia((prev) => prev.map((item) => (item.id === frame.id ? { ...item, ...data.media } : item)));
        if (lightbox?.id === frame.id) {
          setLightbox((prev) => (prev ? { ...prev, ...data.media } : prev));
          setCaption(data.media.caption || '');
        }
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || t('memories.photoMetaFailed'), { variant: 'error' });
      throw err;
    }
  };

  const savePhotoMeta = async () => {
    if (!lightbox || viewOnly) return;
    setSavingMeta(true);
    try {
      const { data } = await api.patch(`/memories/media/${lightbox.id}`, {
        caption,
        contextNotes,
      });
      if (data.media) {
        setMedia((prev) => prev.map((item) => (item.id === lightbox.id ? { ...item, ...data.media } : item)));
        setLightbox((prev) => (prev ? { ...prev, ...data.media } : prev));
        setCaption(data.media.caption || '');
        setContextNotes(data.media.context_notes || '');
        enqueueSnackbar(t('memories.photoMetaSaved'), { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || t('memories.photoMetaFailed'), { variant: 'error' });
    } finally {
      setSavingMeta(false);
    }
  };

  const applyLookToLightbox = async (lookId) => {
    if (!lightbox || viewOnly) return;
    const visualFilter = normalizeVisualLook(lookId);
    const previousFilter = lightbox.visual_filter || null;
    const previousTone = lightbox.visual_tone || null;
    setLightbox((prev) => (prev ? { ...prev, visual_filter: visualFilter, visual_tone: previousTone } : prev));
    setMedia((prev) => prev.map((item) => (
      item.id === lightbox.id
        ? { ...item, visual_filter: visualFilter, visual_tone: item.visual_tone ?? previousTone }
        : item
    )));
    setApplyingLook(true);
    try {
      const { data } = await api.patch(`/memories/media/${lightbox.id}`, { visualFilter });
      if (data.media) {
        setMedia((prev) => prev.map((item) => (item.id === lightbox.id ? { ...item, ...data.media } : item)));
        setLightbox((prev) => (prev ? { ...prev, ...data.media } : prev));
      }
    } catch {
      setLightbox((prev) => (prev ? { ...prev, visual_filter: previousFilter, visual_tone: previousTone } : prev));
      setMedia((prev) => prev.map((item) => (
        item.id === lightbox.id
          ? { ...item, visual_filter: previousFilter, visual_tone: previousTone }
          : item
      )));
      setLookMessage(t('memories.filterFailed'));
    } finally {
      setApplyingLook(false);
    }
  };

  const applyToneToLightbox = async (toneId) => {
    if (!lightbox || viewOnly) return;
    const visualTone = normalizeVisualTone(toneId);
    const previousFilter = lightbox.visual_filter || null;
    const previousTone = lightbox.visual_tone || null;
    setLightbox((prev) => (prev ? { ...prev, visual_filter: previousFilter, visual_tone: visualTone } : prev));
    setMedia((prev) => prev.map((item) => (
      item.id === lightbox.id
        ? { ...item, visual_filter: item.visual_filter ?? previousFilter, visual_tone: visualTone }
        : item
    )));
    setApplyingLook(true);
    try {
      const { data } = await api.patch(`/memories/media/${lightbox.id}`, { visualTone });
      if (data.media) {
        setMedia((prev) => prev.map((item) => (item.id === lightbox.id ? { ...item, ...data.media } : item)));
        setLightbox((prev) => (prev ? { ...prev, ...data.media } : prev));
      }
    } catch {
      setLightbox((prev) => (prev ? { ...prev, visual_filter: previousFilter, visual_tone: previousTone } : prev));
      setMedia((prev) => prev.map((item) => (
        item.id === lightbox.id
          ? { ...item, visual_filter: previousFilter, visual_tone: previousTone }
          : item
      )));
      setLookMessage(t('memories.filterFailed'));
    } finally {
      setApplyingLook(false);
    }
  };

  const removeFrame = async (frame) => {
    await api.delete(`/memories/media/${frame.id}`);
    setMedia((prev) => prev.filter((item) => item.id !== frame.id));
    setLightbox(null);
  };

  const deleteAlbum = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await api.delete(`/memories/events/${slug}`);
      setDeleteOpen(false);
      if (embedded && event?.trip_id) {
        navigate(`/trips/${event.trip_id}`, { replace: true });
      } else {
        navigate(memoriesPath(), { replace: true });
      }
    } catch (err) {
      setDeleteError(err.response?.data?.error || t('memories.deleteAlbumFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const { query: photoQuery, filtered: matchedMedia } = useMemo(
    () => filterAlbumFrames(media, photoSearch),
    [media, photoSearch]
  );
  const filteredMedia = useMemo(
    () => sortAlbumFrames(matchedMedia, photoSort),
    [matchedMedia, photoSort]
  );
  const favorites = useMemo(() => filteredMedia.filter((frame) => frame.starred), [filteredMedia]);
  const rest = useMemo(() => filteredMedia.filter((frame) => !frame.starred), [filteredMedia]);
  const locale = typeof navigator !== 'undefined' ? navigator.language : undefined;
  const groupedFavorites = useMemo(
    () => groupAlbumFrames(favorites, photoSort, { showLabels: showGroupLabels, locale }),
    [favorites, photoSort, showGroupLabels, locale]
  );
  const groupedRest = useMemo(
    () => groupAlbumFrames(rest, photoSort, { showLabels: showGroupLabels, locale }),
    [rest, photoSort, showGroupLabels, locale]
  );
  const peopleCount = members.length + pendingInvites.length + declinedInvites.length;

  const openFrame = (frame) => {
    setLightbox(frame);
    setLightboxZoom(1);
    setCaption(frame.caption || '');
    setContextNotes(frame.context_notes || '');
    setInfoOpen(false);
  };

  const filterLabelFor = useCallback((frameOrFilterId) => {
    if (frameOrFilterId && typeof frameOrFilterId === 'object') {
      return describeLookTone(frameOrFilterId.visual_filter, frameOrFilterId.visual_tone, t);
    }
    // Legacy single-id callers (look or tone meta label)
    const meta = getVisualFilterMeta(frameOrFilterId);
    return meta ? t(meta.labelKey) : '';
  }, [t]);

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setPendingLook(null);
    setPendingTone(null);
    setLookMessage('');
  };

  const toggleSelect = (frame) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(frame.id)) next.delete(frame.id);
      else next.add(frame.id);
      return next;
    });
  };

  const selectVisible = () => {
    setSelectedIds(new Set(filteredMedia.map((frame) => frame.id)));
  };

  const applyLookToSelected = async (lookId) => {
    if (selectedIds.size === 0) {
      setLookMessage(t('memories.filterSelectPhotos'));
      return;
    }
    setApplyingLook(true);
    setLookMessage('');
    try {
      const visualFilter = normalizeVisualLook(lookId);
      await api.post(`/memories/events/${slug}/media/visual-filters`, {
        mediaIds: [...selectedIds],
        visualFilter,
      });
      setMedia((prev) => prev.map((frame) => (
        selectedIds.has(frame.id) ? { ...frame, visual_filter: visualFilter } : frame
      )));
      setLookMessage(
        visualFilter
          ? t('memories.filterApplied', { count: selectedIds.size })
          : t('memories.filterCleared', { count: selectedIds.size })
      );
      setPendingLook(visualFilter);
    } catch (err) {
      setLookMessage(err.response?.data?.error || t('memories.filterFailed'));
    } finally {
      setApplyingLook(false);
    }
  };

  const applyToneToSelected = async (toneId) => {
    if (selectedIds.size === 0) {
      setLookMessage(t('memories.filterSelectPhotos'));
      return;
    }
    setApplyingLook(true);
    setLookMessage('');
    try {
      const visualTone = normalizeVisualTone(toneId);
      await api.post(`/memories/events/${slug}/media/visual-filters`, {
        mediaIds: [...selectedIds],
        visualTone,
      });
      setMedia((prev) => prev.map((frame) => (
        selectedIds.has(frame.id) ? { ...frame, visual_tone: visualTone } : frame
      )));
      setLookMessage(
        visualTone
          ? t('memories.filterApplied', { count: selectedIds.size })
          : t('memories.filterCleared', { count: selectedIds.size })
      );
      setPendingTone(visualTone);
    } catch (err) {
      setLookMessage(err.response?.data?.error || t('memories.filterFailed'));
    } finally {
      setApplyingLook(false);
    }
  };

  if (loading) {
    return <Typography sx={{ color: DOCUMENT.ink3 }}>{t('common.loading')}</Typography>;
  }
  if (error) {
    return <Typography sx={{ color: '#9C3B2A' }}>{error}</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        {!embedded && !viewOnly && (
          <Button component={RouterLink} to={memoriesPath()} sx={{ color: DOCUMENT.ink3, letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11 }}>
            ← {t('nav.memories')}
          </Button>
        )}
        {!viewOnly && (
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexWrap: 'wrap', alignItems: 'center' }}>
            {event.is_owner && (
              <Button
                onClick={copyShareLink}
                startIcon={<IosShareOutlinedIcon sx={{ fontSize: 18 }} />}
                sx={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink2 }}
              >
                {t('memories.copyShareLink')}
              </Button>
            )}
            <Button
              onClick={openQr}
              startIcon={<QrCode2OutlinedIcon sx={{ fontSize: 18 }} />}
              sx={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink2 }}
            >
              {t('memories.qrCard')}
            </Button>
            {media.length > 0 && (
              <Button
                onClick={() => {
                  setSelectMode(true);
                  setLookMessage('');
                  setPendingLook(null);
                  setPendingTone(null);
                }}
                startIcon={<AutoFixHighOutlinedIcon sx={{ fontSize: 18 }} />}
                sx={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink2 }}
              >
                {t('memories.applyLooks')}
              </Button>
            )}
            {event.is_owner && (
              <Button
                component={RouterLink}
                to={memoriesPath(`e/${slug}/edit`)}
                startIcon={<EditOutlinedIcon sx={{ fontSize: 18 }} />}
                sx={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink2 }}
              >
                {t('common.edit')}
              </Button>
            )}
            {(event.is_primary_owner ?? event.is_owner) && (
              <Button
                onClick={() => { setDeleteError(''); setDeleteOpen(true); }}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                sx={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11, color: '#9C3B2A' }}
              >
                {t('memories.deleteAlbum')}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {(shareMessage || shareLinkUrl) && (
        <Box
          sx={{
            mb: 1.5,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
          }}
          data-testid="memories-share-link-banner"
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {shareMessage && (
              <Typography sx={{ color: DOCUMENT.ink2, fontSize: 13, mb: shareLinkUrl ? 1 : 0 }}>
                {shareMessage}
              </Typography>
            )}
            {shareLinkUrl && (
              <TextField
                fullWidth
                size="small"
                value={shareLinkUrl}
                onFocus={(e) => e.target.select()}
                InputProps={{ readOnly: true }}
                inputProps={{
                  'data-testid': 'memories-share-link-url',
                  'aria-label': t('memories.shareLinkLabel'),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: 13,
                    color: DOCUMENT.ink,
                    backgroundColor: 'rgba(0,0,0,0.02)',
                  },
                }}
              />
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setShareMessage('');
              setShareLinkUrl('');
            }}
            aria-label={t('common.close')}
            data-testid="memories-share-link-dismiss"
            sx={{ color: DOCUMENT.ink3, mt: -0.25 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {viewOnly && (
        <Typography sx={{ letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.accent, mb: 2 }}>
          {guestCanUpload ? t('memories.guestLinkBanner') : t('memories.viewOnlyBanner')}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, borderBottom: `1px solid ${DOCUMENT.rule}`, pb: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: embedded ? 30 : { xs: 32, md: 40 }, color: DOCUMENT.ink }}>
            {event.name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {!embedded && !viewOnly && event.trip_id && !isMemoriesHost() && (
            <Button
              component="a"
              href={mainAppTripUrl(event.trip_id)}
              sx={{
                px: 1.5,
                py: 0.75,
                minWidth: 0,
                border: `1px solid ${DOCUMENT.accent}`,
                color: DOCUMENT.accent,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                fontSize: 10,
                borderRadius: '2px',
                textDecoration: 'none',
                '&:hover': { backgroundColor: 'rgba(138, 106, 40, 0.08)' },
              }}
            >
              {t('memories.viewLinkedTrip')}
            </Button>
          )}
          <Typography sx={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3 }}>
            {event.frame_count || media.length} {t('memories.frames')}
            {!viewOnly && (
              <> · {event.member_count || members.length || 0} {t('memories.contributors')}</>
            )}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 3, maxWidth: 720 }} data-testid="memories-album-intro">
        {editingIntro ? (
          <Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              value={introDraft}
              onChange={(e) => setIntroDraft(e.target.value.slice(0, ALBUM_INTRO_MAX))}
              placeholder={t('memories.albumIntroPlaceholder')}
              inputProps={{
                'aria-label': t('memories.albumIntroPlaceholder'),
                'data-testid': 'memories-album-intro-input',
                maxLength: ALBUM_INTRO_MAX,
              }}
              sx={{
                mb: 1.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '2px',
                  backgroundColor: '#fff',
                  fontFamily: DOCUMENT.display,
                  fontStyle: 'italic',
                  fontSize: { xs: 17, md: 19 },
                  lineHeight: 1.7,
                  color: DOCUMENT.ink2,
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                onClick={saveIntro}
                disabled={savingIntro}
                data-testid="memories-album-intro-save"
                sx={{
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  fontSize: 11,
                  color: DOCUMENT.ivory,
                  backgroundColor: DOCUMENT.oxblood,
                  borderRadius: '2px',
                  px: 2,
                  '&:hover': { backgroundColor: '#5C1A22' },
                }}
              >
                {t('memories.albumIntroSave')}
              </Button>
              <Button
                onClick={cancelEditIntro}
                disabled={savingIntro}
                sx={{ letterSpacing: '0.14em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3 }}
              >
                {t('common.cancel')}
              </Button>
              {introMessage && (
                <Typography sx={{ color: '#9C3B2A', fontSize: 13 }}>{introMessage}</Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box>
            {event.notes ? (
              <Typography
                component="p"
                sx={{
                  fontFamily: DOCUMENT.display,
                  fontStyle: 'italic',
                  fontWeight: 400,
                  fontSize: embedded ? 17 : { xs: 17, md: 20 },
                  lineHeight: 1.75,
                  color: DOCUMENT.ink2,
                  letterSpacing: '0.01em',
                  whiteSpace: 'pre-wrap',
                  m: 0,
                }}
              >
                {event.notes}
              </Typography>
            ) : (
              event.is_owner && !viewOnly && (
                <Typography sx={{ fontFamily: DOCUMENT.display, fontStyle: 'italic', fontSize: 16, color: DOCUMENT.ink3, m: 0 }}>
                  {t('memories.albumIntroEmpty')}
                </Typography>
              )
            )}
            {event.is_owner && !viewOnly && (
              <Button
                onClick={beginEditIntro}
                startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                data-testid="memories-album-intro-edit"
                sx={{
                  mt: 1,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  fontSize: 10,
                  color: DOCUMENT.ink3,
                  px: 0,
                  minWidth: 0,
                }}
              >
                {event.notes ? t('memories.albumIntroEdit') : t('memories.albumIntroAdd')}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {!viewOnly && (
        <Tabs
          value={albumTab}
          onChange={(_, next) => setAlbumTab(next)}
          sx={{ ...documentSubTabsSx, mb: 3 }}
        >
          <Tab label={t('memories.tabPhotos', { count: media.length })} />
          <Tab label={t('memories.tabPeople', { count: peopleCount })} />
        </Tabs>
      )}

      {(viewOnly || albumTab === 0) && (
        <Box>
          {(media.length > 0 || canUpload || showGuestGate) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                mb: (showUpload && canUpload) || showGuestGate ? 1 : 2.5,
                minHeight: 40,
              }}
            >
              {canUpload && (
                <Button
                  onClick={() => setUploadVisible(!showUpload)}
                  startIcon={showUpload
                    ? <UnfoldLessOutlinedIcon sx={{ fontSize: 18 }} />
                    : <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 18 }} />}
                  data-testid="memories-toggle-upload"
                  sx={{
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    fontSize: 11,
                    color: DOCUMENT.ink3,
                  }}
                >
                  {showUpload ? t('memories.hideUpload') : t('memories.showUpload')}
                </Button>
              )}

              {media.length > 0 && (
                <>
                  <Button
                    onClick={() => setSlideshowOpen(true)}
                    startIcon={<SlideshowOutlinedIcon sx={{ fontSize: 18 }} />}
                    data-testid="memories-slideshow-open"
                    sx={{
                      letterSpacing: '0.14em',
                      textTransform: 'uppercase',
                      fontSize: 11,
                      color: DOCUMENT.ink3,
                    }}
                  >
                    {t('memories.slideshow')}
                  </Button>

                  <FormControl
                    size="small"
                    sx={{ ...documentSelectSx, minWidth: { xs: 148, sm: 180 } }}
                    data-testid="memories-photo-sort"
                  >
                    <InputLabel id="memories-photo-sort-label">{t('memories.photoSortLabel')}</InputLabel>
                    <Select
                      labelId="memories-photo-sort-label"
                      label={t('memories.photoSortLabel')}
                      value={photoSort}
                      onChange={(event) => setPhotoSort(event.target.value)}
                      MenuProps={documentSelectMenuProps}
                      inputProps={{ 'aria-label': t('memories.photoSortLabel') }}
                    >
                      <MenuItem value="album">{t('memories.photoSortAlbum')}</MenuItem>
                      <MenuItem value="newest">{t('memories.photoSortNewest')}</MenuItem>
                      <MenuItem value="oldest">{t('memories.photoSortOldest')}</MenuItem>
                      <MenuItem value="participant">{t('memories.photoSortParticipant')}</MenuItem>
                      <MenuItem value="participant_desc">{t('memories.photoSortParticipantDesc')}</MenuItem>
                      <MenuItem value="caption">{t('memories.photoSortCaption')}</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={(
                      <Switch
                        size="small"
                        checked={showGroupLabels}
                        onChange={(event) => setShowGroupLabels(event.target.checked)}
                        inputProps={{
                          'aria-label': t('memories.photoLabelsLabel'),
                          'data-testid': 'memories-photo-labels',
                        }}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: DOCUMENT.oxblood },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: DOCUMENT.oxblood,
                          },
                        }}
                      />
                    )}
                    label={t('memories.photoLabelsLabel')}
                    sx={{
                      m: 0,
                      ml: 0.5,
                      color: DOCUMENT.ink2,
                      '& .MuiFormControlLabel-label': {
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontSize: 11,
                        color: DOCUMENT.ink3,
                        fontFamily: DOCUMENT.sans,
                        whiteSpace: 'nowrap',
                      },
                    }}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      overflow: 'hidden',
                      width: searchOpen || photoSearch ? { xs: 'min(100%, 300px)', sm: 320 } : 40,
                      maxWidth: '100%',
                      transition: 'width 0.22s ease',
                    }}
                  >
                    {(searchOpen || photoSearch) ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={photoSearch}
                        onChange={(event) => setPhotoSearch(event.target.value)}
                        onBlur={() => {
                          if (!photoSearch.trim()) setSearchOpen(false);
                        }}
                        placeholder={t('memories.photoSearchPlaceholder')}
                        inputRef={photoSearchInputRef}
                        InputProps={{
                          startAdornment: (
                            <SearchIcon sx={{ fontSize: 18, color: DOCUMENT.ink3, mr: 0.75 }} />
                          ),
                          endAdornment: (
                            <IconButton
                              size="small"
                              aria-label={t('common.close')}
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={closePhotoSearch}
                              sx={{ color: DOCUMENT.ink3 }}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          ),
                        }}
                        inputProps={{
                          'aria-label': t('memories.photoSearchPlaceholder'),
                          'data-testid': 'memories-photo-search',
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 0,
                            backgroundColor: DOCUMENT.surface,
                            fontSize: 13,
                            pr: 0.5,
                            '& fieldset': { borderColor: DOCUMENT.rule },
                            '&:hover fieldset': { borderColor: DOCUMENT.ruleHard },
                            '&.Mui-focused fieldset': { borderColor: DOCUMENT.oxblood, borderWidth: 1 },
                          },
                        }}
                      />
                    ) : (
                      <IconButton
                        onClick={openPhotoSearch}
                        aria-label={t('memories.photoSearchPlaceholder')}
                        data-testid="memories-photo-search-open"
                        sx={{ color: DOCUMENT.ink3 }}
                      >
                        <SearchIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}

          {showGuestGate && (
            <MemoriesGuestGate
              inviteCode={shareCode}
              returnUrl={typeof window !== 'undefined' ? window.location.href : undefined}
              showGuestForm={guestCanUpload}
              onJoined={async () => {
                setLoading(true);
                try {
                  await load();
                } catch (err) {
                  setError(err.response?.data?.error || t('memories.loadFailed'));
                } finally {
                  setLoading(false);
                }
              }}
            />
          )}
          {canUpload && showUpload && (
            <MemoriesUploadPanel
              slug={slug}
              compact={media.length > 0}
              storageUsedBytes={event.storage_used_bytes}
              storageQuotaMb={event.storage_quota_mb}
              onUploaded={load}
            />
          )}

          {selectMode && !viewOnly && (
            <Box
              data-testid="memories-look-toolbar"
              sx={{
                mb: 2.5,
                p: 2,
                border: `1px solid ${DOCUMENT.rule}`,
                backgroundColor: DOCUMENT.surface,
                borderRadius: '2px',
              }}
            >
              <Typography sx={{ letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.ink3, mb: 1 }}>
                {t('memories.filterSelectHint', { count: selectedIds.size })}
              </Typography>
              <Typography sx={{ letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 10, color: DOCUMENT.ink3, mb: 0.75 }}>
                {t('memories.applyLooks')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                {MEMORIES_LOOKS.map((look) => (
                  <Button
                    key={look.id}
                    disabled={applyingLook || selectedIds.size === 0}
                    onClick={() => applyLookToSelected(look.id)}
                    variant={pendingLook === look.id ? 'contained' : 'outlined'}
                    sx={{
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontSize: 10,
                      borderRadius: '2px',
                      borderColor: DOCUMENT.rule,
                      color: pendingLook === look.id ? DOCUMENT.ivory : DOCUMENT.ink2,
                      backgroundColor: pendingLook === look.id ? DOCUMENT.oxblood : 'transparent',
                      '&:hover': {
                        borderColor: DOCUMENT.accent,
                        backgroundColor: pendingLook === look.id ? '#5C1A22' : 'rgba(138, 106, 40, 0.06)',
                      },
                    }}
                  >
                    {t(look.labelKey)}
                  </Button>
                ))}
                <Button
                  disabled={applyingLook || selectedIds.size === 0}
                  onClick={() => applyLookToSelected(null)}
                  sx={{
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontSize: 10,
                    color: DOCUMENT.ink3,
                  }}
                >
                  {t('memories.filterNone')}
                </Button>
              </Box>
              <Typography sx={{ letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 10, color: DOCUMENT.ink3, mb: 0.75 }}>
                {t('memories.applyTones')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                {MEMORIES_TONES.map((tone) => (
                  <Button
                    key={tone.id}
                    disabled={applyingLook || selectedIds.size === 0}
                    onClick={() => applyToneToSelected(tone.id)}
                    variant={pendingTone === tone.id ? 'contained' : 'outlined'}
                    sx={{
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontSize: 10,
                      borderRadius: '2px',
                      borderColor: DOCUMENT.rule,
                      color: pendingTone === tone.id ? DOCUMENT.ivory : DOCUMENT.ink2,
                      backgroundColor: pendingTone === tone.id ? DOCUMENT.oxblood : 'transparent',
                      '&:hover': {
                        borderColor: DOCUMENT.accent,
                        backgroundColor: pendingTone === tone.id ? '#5C1A22' : 'rgba(138, 106, 40, 0.06)',
                      },
                    }}
                  >
                    {t(tone.labelKey)}
                  </Button>
                ))}
                <Button
                  disabled={applyingLook || selectedIds.size === 0}
                  onClick={() => applyToneToSelected(null)}
                  sx={{
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontSize: 10,
                    color: DOCUMENT.ink3,
                  }}
                >
                  {t('memories.filterNoTone')}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Button
                  disabled={applyingLook || filteredMedia.length === 0}
                  onClick={selectVisible}
                  sx={{ letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 10, color: DOCUMENT.ink2 }}
                >
                  {t('memories.filterSelectAll')}
                </Button>
                <Button
                  disabled={applyingLook}
                  onClick={() => setSelectedIds(new Set())}
                  sx={{ letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 10, color: DOCUMENT.ink2 }}
                >
                  {t('memories.filterClearSelection')}
                </Button>
                <Button
                  disabled={applyingLook}
                  onClick={exitSelectMode}
                  sx={{ letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 10, color: DOCUMENT.ink3, ml: 'auto' }}
                >
                  {t('common.done')}
                </Button>
              </Box>
              {lookMessage && (
                <Typography sx={{ color: DOCUMENT.ink2, fontSize: 13, mt: 1.25 }}>{lookMessage}</Typography>
              )}
            </Box>
          )}

          {media.length === 0 && (
            <Typography sx={{ color: DOCUMENT.ink2, mb: 4 }}>
              {viewOnly && !guestCanUpload ? t('memories.emptyGalleryViewOnly') : t('memories.emptyGallery')}
            </Typography>
          )}

          {media.length > 0 && photoQuery && filteredMedia.length === 0 && (
            <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 4 }} data-testid="memories-photo-search-empty">
              {t('memories.photoSearchEmpty')}
            </Typography>
          )}

          {favorites.length > 0 && (
            <Box sx={{ mb: 3 }} data-testid="memories-favorites">
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 23, color: DOCUMENT.ink, mb: 1.5 }}>
                {t('memories.favorites')}
                {photoQuery ? ` · ${favorites.length}` : ''}
              </Typography>
              {groupedFavorites.map((group) => {
                const label = resolveGroupLabel(group.label, t);
                return (
                  <Box
                    key={`fav-${group.key}`}
                    sx={{ mb: showGroupLabels && label ? 2.5 : 0 }}
                    data-testid={`memories-group-favorites-${group.key}`}
                  >
                    {showGroupLabels && label && (
                      <Typography
                        component="h3"
                        sx={{
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          fontSize: 11,
                          color: DOCUMENT.ink3,
                          mb: 1.25,
                          pb: 0.75,
                          borderBottom: `1px solid ${DOCUMENT.rule}`,
                        }}
                      >
                        {label}
                        {` · ${group.frames.length}`}
                      </Typography>
                    )}
                    <PhotoGrid
                      frames={group.frames}
                      onOpen={openFrame}
                      testId={`memories-favorites-grid-${group.key}`}
                      photoLabel={t('memories.frame')}
                      filterLabelFor={filterLabelFor}
                      selectMode={selectMode && !viewOnly}
                      selectedIds={selectedIds}
                      onToggleSelect={toggleSelect}
                      canEditCaption={!viewOnly}
                      onSaveCaption={saveFrameCaption}
                    />
                  </Box>
                );
              })}
            </Box>
          )}

          {rest.length > 0 && (
            <Box data-testid="memories-grid">
              {favorites.length > 0 && (
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 23, color: DOCUMENT.ink, mb: 1.5 }}>
                  {t('memories.allPhotos')}
                  {photoQuery ? ` · ${rest.length}` : ''}
                </Typography>
              )}
              {groupedRest.map((group) => {
                const label = resolveGroupLabel(group.label, t);
                return (
                  <Box
                    key={`rest-${group.key}`}
                    sx={{ mb: showGroupLabels && label ? 2.5 : 0 }}
                    data-testid={`memories-group-${group.key}`}
                  >
                    {showGroupLabels && label && (
                      <Typography
                        component="h3"
                        sx={{
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          fontSize: 11,
                          color: DOCUMENT.ink3,
                          mb: 1.25,
                          pb: 0.75,
                          borderBottom: `1px solid ${DOCUMENT.rule}`,
                        }}
                      >
                        {label}
                        {` · ${group.frames.length}`}
                      </Typography>
                    )}
                    <PhotoGrid
                      frames={group.frames}
                      onOpen={openFrame}
                      testId={`memories-grid-${group.key}`}
                      photoLabel={t('memories.frame')}
                      filterLabelFor={filterLabelFor}
                      selectMode={selectMode && !viewOnly}
                      selectedIds={selectedIds}
                      onToggleSelect={toggleSelect}
                      canEditCaption={!viewOnly}
                      onSaveCaption={saveFrameCaption}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {!viewOnly && albumTab === 1 && (
        <MemoriesPeoplePanel
          isOwner={Boolean(event.is_owner)}
          members={members}
          pendingInvites={pendingInvites}
          declinedInvites={declinedInvites}
          inviteEmails={inviteEmails}
          onInviteEmailsChange={setInviteEmails}
          inviteDraft={inviteDraft}
          onInviteDraftChange={setInviteDraft}
          inviting={inviting}
          inviteMessage={inviteMessage}
          onSendInvites={sendInvites}
          busyId={peopleBusyId}
          onRemoveMember={removeMember}
          onRevokeInvite={revokeInvite}
          onResendInvite={resendInvite}
          canAssignOwners={Boolean(event.is_owner) && !event.trip_id}
          primaryOwnerId={event.owner_user_id ?? null}
          currentUserId={authUser?.id ?? null}
          onUpdateMemberRole={updateMemberRole}
        />
      )}

      <MemoriesSlideshow
        open={slideshowOpen}
        frames={filteredMedia}
        startIndex={0}
        musicSrc={event?.slideshow_music_url ? mediaUrl(event.slideshow_music_url) : null}
        onClose={() => setSlideshowOpen(false)}
      />

      <Dialog
        open={Boolean(lightbox)}
        onClose={() => { setLightbox(null); setInfoOpen(false); setLightboxZoom(1); }}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          'data-testid': 'memories-lightbox-paper',
          sx: {
            display: 'flex',
            flexDirection: 'column',
            maxWidth: { xs: '100%', sm: 720 },
            width: { xs: 'calc(100% - 16px)', sm: '100%' },
            m: { xs: 1, sm: 2 },
            maxHeight: { xs: 'min(88dvh, 88vh)', sm: 'min(90dvh, 90vh)' },
            overflow: 'hidden',
            borderRadius: 0,
            backgroundColor: '#1A1210',
          },
        }}
      >
        {lightbox && (
          <DialogContent
            data-testid="memories-lightbox-content"
            sx={{
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              backgroundColor: '#1A1210',
              p: 0,
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 3,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 0.25,
                px: { xs: 1, sm: 1.5 },
                py: 0.75,
                background: 'linear-gradient(180deg, rgba(26,18,16,0.96) 55%, rgba(26,18,16,0) 100%)',
              }}
            >
              <IconButton
                aria-label={t('memories.zoomOut')}
                onClick={() => setLightboxZoom((z) => Math.max(0.75, Math.round((z - 0.25) * 100) / 100))}
                disabled={lightboxZoom <= 0.75}
                sx={{ color: DOCUMENT.ivory }}
                size="small"
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label={t('memories.zoomIn')}
                onClick={() => setLightboxZoom((z) => Math.min(2.5, Math.round((z + 0.25) * 100) / 100))}
                disabled={lightboxZoom >= 2.5}
                sx={{ color: DOCUMENT.ivory }}
                size="small"
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => { setLightbox(null); setInfoOpen(false); setLightboxZoom(1); }}
                sx={{ color: DOCUMENT.ivory }}
                size="small"
                aria-label={t('common.close')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ position: 'relative', px: { xs: 1.5, sm: 2 }, pt: 0.5, pb: 1 }}>
              {(() => {
                const look = getVisualFilterPresentation(lightbox.visual_filter, lightbox.visual_tone);
                const isFramed = Boolean(look.usesCard || look.usesFilmStrip || look.usesRusticPrint);
                const frameLabel = describeLookTone(lightbox.visual_filter, lightbox.visual_tone, t);
                return (
                  <Box
                    sx={{
                      transform: `scale(${lightboxZoom})`,
                      transformOrigin: 'top center',
                      transition: 'transform 0.15s ease',
                      maxWidth: look.usesFilmStrip
                        ? { xs: '96%', sm: 640 }
                        : look.usesRusticPrint
                          ? { xs: '78%', sm: 360 }
                          : look.usesCard
                            ? { xs: '78%', sm: 360 }
                            : '100%',
                      width: isFramed ? '100%' : 'auto',
                      mx: 'auto',
                      my: isFramed ? { xs: 1, sm: 2 } : 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        ...(isFramed ? look.frameSx : {}),
                        ...(look.usesFilmStrip
                          ? { aspectRatio: look.outerAspectRatio || FILM_STRIP.outerAspectRatio, width: '100%' }
                          : look.usesRusticPrint
                            ? {
                              width: '100%',
                              maxWidth: { xs: 260, sm: 340 },
                              mx: 'auto',
                              aspectRatio: look.outerAspectRatio || RUSTIC_PRINT.outerAspectRatio,
                            }
                            : look.usesCard
                              ? {
                                width: '100%',
                                maxWidth: { xs: 260, sm: 340 },
                                mx: 'auto',
                                aspectRatio: look.outerAspectRatio || INSTANT_CARD.outerAspectRatio,
                              }
                              : {}),
                      }}
                    >
                    <Box
                      sx={{
                        position: 'relative',
                        ...(look.usesFilmStrip
                          ? {
                            mx: `${FILM_STRIP.sidePadPct}%`,
                            mt: `${FILM_STRIP.sprocketPadPct}%`,
                            mb: `${FILM_STRIP.sprocketPadPct}%`,
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflow: 'hidden',
                            backgroundColor: 'transparent',
                          }
                          : look.usesRusticPrint
                            ? {
                              position: 'absolute',
                              left: look.inset?.left || RUSTIC_PRINT.inset.left,
                              top: look.inset?.top || RUSTIC_PRINT.inset.top,
                              right: look.inset?.right || RUSTIC_PRINT.inset.right,
                              bottom: look.inset?.bottom || RUSTIC_PRINT.inset.bottom,
                              overflow: 'hidden',
                              backgroundColor: '#2A2218',
                            }
                            : look.usesCard
                              ? {
                                mx: `${INSTANT_CARD.edgePct}%`,
                                mt: `${INSTANT_CARD.edgePct}%`,
                                aspectRatio: INSTANT_CARD.photoAspectRatio || '1',
                                width: 'auto',
                                overflow: 'hidden',
                                flexShrink: 0,
                                backgroundColor: '#1A120C',
                                border: '1px solid rgba(0,0,0,0.14)',
                                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.2)',
                              }
                              : {
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                                lineHeight: 0,
                              }),
                      }}
                    >
                      {look.usesFilmStrip ? (
                        <MemoriesFilmStripFrames
                          src={mediaUrl(lightbox.view_url)}
                          alt={lightbox.caption || ''}
                          imageSx={look.imageSx}
                          overlaySx={look.overlaySx}
                          toneOverlaySx={look.toneOverlaySx}
                          mode="img"
                        />
                      ) : (
                        <>
                          <Box
                            component="img"
                            src={mediaUrl(lightbox.view_url)}
                            alt={lightbox.caption || ''}
                            sx={{
                              display: 'block',
                              ...(look.usesCard || look.usesRusticPrint
                                ? {
                                  position: 'absolute',
                                  inset: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  objectPosition: 'center',
                                }
                                : {
                                  maxWidth: '100%',
                                  maxHeight: { xs: '42vh', sm: '56vh' },
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain',
                                  objectPosition: 'center',
                                  background: '#110C0A',
                                }),
                              ...look.imageSx,
                            }}
                          />
                          {look.overlaySx && (
                            <Box
                              aria-hidden
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: 'none',
                                ...look.overlaySx,
                              }}
                            />
                          )}
                          {look.toneOverlaySx && (
                            <Box
                              aria-hidden
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: 'none',
                                ...look.toneOverlaySx,
                              }}
                            />
                          )}
                        </>
                      )}
                    </Box>
                    {look.usesRusticPrint && (
                      <Box
                        component="img"
                        src={look.overlayUrl || RUSTIC_PRINT.overlayUrl}
                        alt=""
                        aria-hidden
                        data-testid="memories-rustic-print-overlay"
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'fill',
                          pointerEvents: 'none',
                          zIndex: 3,
                        }}
                      />
                    )}
                    {look.usesCard && (
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: '7%',
                          py: 1.5,
                          textAlign: 'center',
                        }}
                      >
                        <ChinCaption
                          caption={lightbox.caption || ''}
                          placeholder={frameLabel}
                          color={look.captionColor || '#3D342C'}
                          canEdit={!viewOnly}
                          onSave={async (next) => {
                            await saveFrameCaption(lightbox, next);
                          }}
                          sx={{ fontSize: { xs: 16, sm: 18 }, lineHeight: 1.3, letterSpacing: lightbox.caption ? '0.01em' : '0.12em' }}
                        />
                      </Box>
                    )}
                    </Box>
                    {(look.usesFilmStrip || look.usesRusticPrint) && (
                      <Box sx={{ mt: 1.5, width: '100%', textAlign: 'center' }}>
                        <ChinCaption
                          caption={lightbox.caption || ''}
                          placeholder={frameLabel}
                          color={DOCUMENT.ivory}
                          canEdit={!viewOnly}
                          onSave={async (next) => {
                            await saveFrameCaption(lightbox, next);
                          }}
                          sx={{ fontSize: { xs: 14, sm: 16 }, lineHeight: 1.3, letterSpacing: lightbox.caption ? '0.01em' : '0.12em' }}
                        />
                      </Box>
                    )}
                    {!isFramed && lightbox.caption && (
                      <Typography sx={{ px: 2, py: 1.25, color: DOCUMENT.ivory, fontSize: 14, backgroundColor: '#1A1210' }}>
                        {lightbox.caption}
                      </Typography>
                    )}
                  </Box>
                );
              })()}
            </Box>
            {!viewOnly && (
              <Box
                data-testid="memories-lightbox-looks"
                sx={{
                  px: { xs: 1.5, sm: 2 },
                  py: 1.5,
                  pb: { xs: 3, sm: 2 },
                  backgroundColor: '#241A17',
                  borderTop: `1px solid rgba(255,255,255,0.08)`,
                }}
              >
                <Box data-testid="memories-photo-meta" sx={{ mb: 2 }}>
                  <Typography sx={{ letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 10, color: 'rgba(255,255,255,0.45)', mb: 1 }}>
                    {t('memories.photoMetaHeading')}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label={t('memories.caption')}
                    helperText={t('memories.captionHelp')}
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    inputProps={{ maxLength: 500, 'data-testid': 'memories-caption-input' }}
                    FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)', mx: 0 } }}
                    sx={{
                      mb: 1.5,
                      '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.55)' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                        color: DOCUMENT.ivory,
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
                        '&.Mui-focused fieldset': { borderColor: DOCUMENT.accent, borderWidth: 1 },
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={5}
                    label={t('memories.contextNotes')}
                    helperText={t('memories.contextNotesHelp')}
                    value={contextNotes}
                    onChange={(e) => setContextNotes(e.target.value)}
                    inputProps={{ maxLength: 2000, 'data-testid': 'memories-context-input' }}
                    FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)', mx: 0 } }}
                    sx={{
                      mb: 1,
                      '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.55)' },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                        color: DOCUMENT.ivory,
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
                        '&.Mui-focused fieldset': { borderColor: DOCUMENT.accent, borderWidth: 1 },
                      },
                    }}
                  />
                  <Button
                    onClick={savePhotoMeta}
                    disabled={savingMeta}
                    data-testid="memories-photo-meta-save"
                    sx={{
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      fontSize: 11,
                      color: DOCUMENT.ivory,
                      backgroundColor: DOCUMENT.oxblood,
                      borderRadius: 0,
                      px: 2,
                      '&:hover': { backgroundColor: '#5C1A22' },
                      '&.Mui-disabled': { color: DOCUMENT.ivory, opacity: 0.45 },
                    }}
                  >
                    {savingMeta ? t('common.loading') : t('memories.savePhotoMeta')}
                  </Button>
                </Box>
                <Typography sx={{ letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 10, color: 'rgba(255,255,255,0.45)', mb: 1 }}>
                  {t('memories.applyLooks')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                  {MEMORIES_LOOKS.map((look) => {
                    const active = normalizeVisualLook(lightbox.visual_filter) === look.id;
                    return (
                      <Button
                        key={look.id}
                        disabled={applyingLook}
                        onClick={() => applyLookToLightbox(look.id)}
                        sx={{
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          fontSize: 10,
                          minWidth: 0,
                          px: 1.25,
                          py: 0.5,
                          borderRadius: '2px',
                          border: `1px solid ${active ? DOCUMENT.accent : 'rgba(255,255,255,0.18)'}`,
                          color: active ? DOCUMENT.ivory : 'rgba(255,255,255,0.75)',
                          backgroundColor: active ? DOCUMENT.oxblood : 'transparent',
                          '&:hover': {
                            borderColor: DOCUMENT.accent,
                            backgroundColor: active ? '#5C1A22' : 'rgba(255,255,255,0.06)',
                          },
                        }}
                      >
                        {t(look.labelKey)}
                      </Button>
                    );
                  })}
                  <Button
                    disabled={applyingLook || !normalizeVisualLook(lightbox.visual_filter)}
                    onClick={() => applyLookToLightbox(null)}
                    sx={{
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontSize: 10,
                      minWidth: 0,
                      px: 1.25,
                      color: 'rgba(255,255,255,0.5)',
                    }}
                  >
                    {t('memories.filterNone')}
                  </Button>
                </Box>
                <Typography sx={{ letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 10, color: 'rgba(255,255,255,0.45)', mb: 1 }}>
                  {t('memories.applyTones')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {MEMORIES_TONES.map((tone) => {
                    const active = normalizeVisualTone(lightbox.visual_tone) === tone.id;
                    return (
                      <Button
                        key={tone.id}
                        disabled={applyingLook}
                        onClick={() => applyToneToLightbox(tone.id)}
                        sx={{
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          fontSize: 10,
                          minWidth: 0,
                          px: 1.25,
                          py: 0.5,
                          borderRadius: '2px',
                          border: `1px solid ${active ? DOCUMENT.accent : 'rgba(255,255,255,0.18)'}`,
                          color: active ? DOCUMENT.ivory : 'rgba(255,255,255,0.75)',
                          backgroundColor: active ? DOCUMENT.oxblood : 'transparent',
                          '&:hover': {
                            borderColor: DOCUMENT.accent,
                            backgroundColor: active ? '#5C1A22' : 'rgba(255,255,255,0.06)',
                          },
                        }}
                      >
                        {t(tone.labelKey)}
                      </Button>
                    );
                  })}
                  <Button
                    disabled={applyingLook || !normalizeVisualTone(lightbox.visual_tone)}
                    onClick={() => applyToneToLightbox(null)}
                    sx={{
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      fontSize: 10,
                      minWidth: 0,
                      px: 1.25,
                      color: 'rgba(255,255,255,0.5)',
                    }}
                  >
                    {t('memories.filterNoTone')}
                  </Button>
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25, backgroundColor: DOCUMENT.surface }}>
              <Button
                onClick={() => setInfoOpen((open) => !open)}
                startIcon={<InfoOutlinedIcon />}
                sx={{ color: DOCUMENT.ink2, letterSpacing: '0.08em', textTransform: 'none', fontSize: 14 }}
              >
                {infoOpen ? t('memories.hideInfo') : t('memories.info')}
              </Button>
              {!viewOnly && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button onClick={() => toggleStar(lightbox)} sx={{ color: lightbox.starred ? DOCUMENT.accent : DOCUMENT.ink2 }}>
                    {lightbox.starred ? t('memories.starred') : t('memories.star')}
                  </Button>
                  <Button onClick={() => removeFrame(lightbox)} sx={{ color: '#9C3B2A' }}>{t('common.delete')}</Button>
                </Box>
              )}
            </Box>
            {infoOpen && (
              <Box sx={{ px: 2.5, py: 2, backgroundColor: DOCUMENT.surface, borderTop: `1px solid ${DOCUMENT.rule}` }}>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, color: DOCUMENT.ink, mb: 1 }}>
                  {lightbox.captured_at
                    ? new Date(lightbox.captured_at).toLocaleString(undefined, {
                      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
                    })
                    : t('memories.undated')}
                </Typography>
                <Typography sx={{ color: DOCUMENT.ink2, mb: 0.5 }}>
                  {t('memories.uploadedBy', { name: lightbox.uploader_display_name, email: lightbox.uploader_email })}
                </Typography>
                {lightbox.original_filename && (
                  <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13 }}>{lightbox.original_filename}</Typography>
                )}
                {(lightbox.width || lightbox.height) && (
                  <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: lightbox.caption || lightbox.context_notes ? 1.5 : 0 }}>
                    {lightbox.width} × {lightbox.height}
                    {lightbox.bytes ? ` · ${(lightbox.bytes / 1024).toFixed(0)} KB` : ''}
                  </Typography>
                )}
                {lightbox.caption && (
                  <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14, mb: lightbox.context_notes ? 1 : 0 }}>
                    <Box component="span" sx={{ letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 10, color: DOCUMENT.ink3, display: 'block', mb: 0.5 }}>
                      {t('memories.caption')}
                    </Box>
                    {lightbox.caption}
                  </Typography>
                )}
                {lightbox.context_notes && (
                  <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14 }} data-testid="memories-info-context">
                    <Box component="span" sx={{ letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 10, color: DOCUMENT.ink3, display: 'block', mb: 0.5 }}>
                      {t('memories.contextNotes')}
                    </Box>
                    {lightbox.context_notes}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
        )}
      </Dialog>

      <Dialog
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: 480,
            m: 2,
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 } }}>
          <Typography sx={{ letterSpacing: '0.24em', textTransform: 'uppercase', fontSize: 10, color: DOCUMENT.accent, mb: 2 }}>
            {t('memories.brand')}
          </Typography>
          <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, sm: 34 }, color: DOCUMENT.oxblood, lineHeight: 1.1 }}>
            {event?.name}
          </Typography>
          <Box sx={{ my: 2.5, p: 1.5, border: `1px solid ${DOCUMENT.rule}`, display: 'inline-block' }}>
            {qr?.joinUrl && (
              <img
                alt="QR"
                width={240}
                height={240}
                src={`https://api.qrserver.com/v1/create-qr-code/?size=240x240&color=4a0e15&bgcolor=ffffff&data=${encodeURIComponent(qr.joinUrl)}`}
              />
            )}
          </Box>
          <Typography sx={{ mb: 1 }}>{t('memories.scanToAdd')}</Typography>
          <Typography sx={{ color: DOCUMENT.oxblood, fontWeight: 500, wordBreak: 'break-all' }}>
            {qr?.joinUrl || `${memoriesPublicBase({ tripLinked: Boolean(event?.trip_id) })}/j/${qr?.code}`}
          </Typography>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, color: DOCUMENT.ink }}>
          {t('memories.deleteAlbumTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: DOCUMENT.ink2 }}>
            {t('memories.deleteAlbumBody', { name: event?.name || '' })}
          </DialogContentText>
          {deleteError && (
            <Typography sx={{ color: '#9C3B2A', fontSize: 13, mt: 1.5 }}>{deleteError}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            disabled={deleting}
            onClick={() => setDeleteOpen(false)}
            sx={{ color: DOCUMENT.ink3, letterSpacing: '0.1em', textTransform: 'uppercase', fontSize: 11 }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            disabled={deleting}
            onClick={deleteAlbum}
            sx={{
              backgroundColor: DOCUMENT.oxblood,
              color: DOCUMENT.ivory,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              fontSize: 11,
              borderRadius: '2px',
              '&:hover': { backgroundColor: '#5C1A22' },
              '&.Mui-disabled': { backgroundColor: DOCUMENT.oxblood, color: DOCUMENT.ivory, opacity: 0.45 },
            }}
          >
            {deleting ? t('common.loading') : t('memories.deleteAlbumConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MemoriesAlbumView;
